import {useEffect, useState, useContext} from "react";
import {azureTokenContext, messageBoxContext} from "../../App";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import MissingSubIdsTree from "./MissingSubIdsTree";
import * as ApiCalls from "./ApiCalls.js";
import {
    ResponsibleAreaResponseType,
    MasterDocument,
    WorkItem,
    MasterDocumentSubId,
    MissingSubidsCollectionType,
} from "./ResponsibilityAreaTable";
import {targetUserIdContext} from "../MainPart";
import {useLogMetric} from "../../hooks/useLogMetric";

type MissingSubIdsModalProps = {
    workItem: WorkItem;
    allMDsWIs: ResponsibleAreaResponseType;
    missingSubIdsCollection: MissingSubidsCollectionType[];
    refreshResponsibleArea: boolean;
    setRefreshResponsibleArea: (value: boolean) => void;
};

export default function MissingSubIdsModal({
                                               workItem,
                                               allMDsWIs,
                                               missingSubIdsCollection,
                                               refreshResponsibleArea,
                                               setRefreshResponsibleArea,
                                           }: MissingSubIdsModalProps) {
    const azureToken = useContext(azureTokenContext);
    const [targetUserId] = useContext(targetUserIdContext);
    const setMessageBox = useContext(messageBoxContext);
    const [open, setOpen] = useState(false);
    const [checkedSubIds, setCheckedSubIds] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [missingSubIds, setMissingSubIds] = useState<MasterDocumentSubId[]>([]);
    const logMetric = useLogMetric();

    useEffect(() => {
        const missingSubids =
            missingSubIdsCollection?.find((item) => item.DocumentId === workItem.DocumentId && item.Source === workItem.Source)
                ?.MissingSubIds || [];
        setMissingSubIds(missingSubids);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetUserId, workItem, allMDsWIs, missingSubIdsCollection, refreshResponsibleArea]);

    const submit = () => {
        setIsSaving(true);
        ApiCalls.AddSubIdsToWorkitem(workItem.Id, checkedSubIds, targetUserId, azureToken)
            .then((response) => {
                if (response.Success === true) {
                    setRefreshResponsibleArea(!refreshResponsibleArea);
                    setMessageBoxFunc("successAddMissingSubIds");
                } else {
                    setMessageBoxFunc("errorAddMissingSubIds");
                    console.error("Failed to add Sub-IDs:", response.Message);
                }
            })
            .catch((error) => {
                setMessageBoxFunc("errorAddMissingSubIds");
                console.error("Error adding Sub-IDs:", error);
            })
            .finally(() => {
                setOpen(false);
                setIsSaving(false);
            });
    };

    const setMessageBoxFunc = (prop: string) => {
        // since it is invocated by changes in prop, I want to make sure that it changes
        setMessageBox("");
        setTimeout(() => {
            setMessageBox(prop);
        }, 10);
    };

    return (
        <div>
            {missingSubIds.length > 0 ? (
                <button onClick={() => {
                    setOpen(true);
                    logMetric({category: "MissingSubIds", subCategory: "Modal opened", message: `User opened missing SubIds modal for WorkItemId: ${workItem.Id}`, workItemId: workItem.Id});
                }} className="ResponsiveAreaTableButton">
                    Legg til bestemmelse
                </button>
            ) : (
                <span className="text-gray-500 text-sm">Ok</span>
            )}

            <Dialog open={open} onClose={setOpen} className="relative z-10">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg data-closed:sm:translate-y-0 data-closed:sm:scale-95"
                        >
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                                            Legg til bestemmelse
                                        </DialogTitle>
                                        <div className="mt-2">
                                            {getRelevantMD(workItem, allMDsWIs) && (
                                                <MissingSubIdsTree
                                                    wi={workItem}
                                                    md={getRelevantMD(workItem, allMDsWIs) as MasterDocument}
                                                    missingSubIds={missingSubIds}
                                                    checkedSubIds={checkedSubIds}
                                                    setCheckedSubIds={setCheckedSubIds}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <button
                                    type="button"
                                    disabled={isSaving || checkedSubIds.length === 0}
                                    onClick={() => {
                                        logMetric({category: "MissingSubIds", subCategory: "Submitted", message: `User submitted missing SubIds for WorkItemId: ${workItem.Id}`, workItemId: workItem.Id});
                                        submit();
                                    }}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-400 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed sm:ml-3 sm:w-auto "
                                >
                                    {isSaving ? "Lagrer..." : "Lagre"}
                                </button>
                                <button
                                    type="button"
                                    data-autofocus
                                    disabled={isSaving}
                                    onClick={() => {
                                        logMetric({category: "MissingSubIds", subCategory: "Modal cancelled", message: `User cancelled missing SubIds modal for WorkItemId: ${workItem.Id}`, workItemId: workItem.Id});
                                        setOpen(false);
                                    }}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-400 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                                >
                                    Avbryt
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

const getRelevantMD = (workItem: WorkItem, allMDsWIs: ResponsibleAreaResponseType) => {
    return allMDsWIs.MasterDocuments.find((md) => md.Source === workItem.Source && md.DocumentId === workItem.DocumentId);
};
